<script setup lang="ts">
import { computed } from 'vue';
import { Icon } from '@iconify/vue';
import { useAuthStore } from '@/stores/authStore';
import { useRouter } from 'vue-router';
import { PerfectScrollbar } from 'vue3-perfect-scrollbar';

const authStore = useAuthStore();
const router = useRouter();

const user = computed(() => authStore.user);
const userInitials = computed(() => {
  const fullname = user.value?.fullname || user.value?.name || user.value?.username;
  if (fullname) {
    return fullname.charAt(0).toUpperCase();
  }
  return 'U';
});

type ProfileMenuItem =
  | {
      title: string
      icon: string
      href: string
    }
  | {
      title: string
      icon: string
      action: () => Promise<void>
    }

const menuItems: ProfileMenuItem[] = [
  {
    title: 'Mon profil',
    href: '/manager/settings',
    icon: 'solar:user-line-duotone',
  },
  {
    title: 'Déconnexion',
    action: async () => {
      await authStore.logout();
      router.push('/auth/login');
    },
    icon: 'solar:logout-2-line-duotone',
  },
];

async function handleItem(item: ProfileMenuItem) {
  if ('href' in item && item.href) {
    await router.push(item.href);
  } else if ('action' in item && item.action) {
    await item.action();
  }
}
</script>

<template>
  <v-menu
    open-on-hover
    open-on-click
  >
    <template #activator="{ props }">
      <v-btn
        variant="text"
        class="custom-hover-primary"
        icon
        v-bind="props"
      >
        <v-avatar
          size="32"
          color="primary"
        >
          <span class="text-white text-body-2 font-weight-bold">{{ userInitials }}</span>
        </v-avatar>
      </v-btn>
    </template>

    <v-sheet
      rounded="md"
      width="280"
      elevation="10"
      class="profile-dropdown"
    >
      <div class="pa-6">
        <div class="d-flex align-center pb-6">
          <v-avatar
            size="55"
            color="primary"
          >
            <span class="text-white text-h6 font-weight-bold">{{ userInitials }}</span>
          </v-avatar>
          <div class="ml-3">
            <h6 class="mb-1">
              {{ user?.fullname ?? user?.email }}
            </h6>
            <span
              v-if="user?.fullname"
              class="text-subtitle-2 text-medium-emphasis"
            >{{ user?.email }}</span>
          </div>
        </div>

        <v-divider />

        <PerfectScrollbar style="max-height: 240px">
          <v-list
            class="py-0 theme-list mt-3"
            lines="one"
          >
            <v-list-item
              v-for="item in menuItems"
              :key="item.title"
              class="py-0 px-2 rounded-md"
              color="primary"
              @click.prevent="handleItem(item)"
            >
              <template #prepend>
                <Icon
                  :icon="item.icon"
                  height="20"
                />
              </template>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </PerfectScrollbar>
      </div>
    </v-sheet>
  </v-menu>
</template>

<style lang="scss" scoped>
.custom-hover-primary {
  &:hover {
    background-color: rgb(var(--v-theme-lightprimary));
    color: rgb(var(--v-theme-primary));
  }
}

.profile-dropdown {
  .v-list-item {
    &:hover {
      background-color: rgb(var(--v-theme-surface-variant));
    }
  }
}
</style>
